import { createFileRoute, useNavigate } from '@tanstack/react-router'
import getSymbolFromCurrency from 'currency-symbol-map'
import { useState } from 'react'

import type { InventoryLevelDetail, VariantSupplier } from '@/api/admin-inventory-types'
import { useDeleteVariantSupplier, useSetPreferredVariantSupplier, useUpsertVariantSupplier } from '@/api/api-mutations'
import { preloadGetInventoryLevel } from '@/api/react-query'
import { useListSuppliers, useListVariantSuppliers } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { CurrencyInput } from '@/components/base/currency-input'
import { Drawer } from '@/components/base/drawer'
import { EmptyState } from '@/components/base/empty-state'
import { Heading } from '@/components/base/heading'
import { IconButton } from '@/components/base/icon-button'
import { Input } from '@/components/base/input'
import { Label } from '@/components/base/label'
import { usePrompt } from '@/components/base/prompt'
import { Select } from '@/components/base/select'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { RouteModal, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'
import { ExclamationCircle } from '@/icons/exclamation-circle'
import { InformationCircle } from '@/icons/information-circle'
import { PenPlus } from '@/icons/pen-plus'
import { fromPrecision, getCurrencyDecimals, toPrecision } from '@/utils/currency'

import { formatAmount, formatMarginPercent } from '../-components'
import { ALL_VALUE } from '../-constants'

export const Route = createFileRoute('/_authenticated/_general/inventory/$inventoryLevelUuid/suppliers')({
	component: ManageSuppliersRoute,
	loader: async ({ context, params }) => {
		await preloadGetInventoryLevel(context, { path: { inventoryLevelUuid: params.inventoryLevelUuid } })
	},
	staticData: {
		crumb: ({ context: { L } }) => L.Inventory.SupplierDialog.Title()
	}
})

function ManageSuppliersRoute() {
	const { inventoryLevelUuid } = Route.useParams()

	return (
		<RouteModal
			prev='/inventory'
			asDrawer
			requiresData
			loader={async api => api.getInventoryLevel({ path: { inventoryLevelUuid } })}
		>
			{data => <ManageSuppliersDrawer level={data} />}
		</RouteModal>
	)
}

function ManageSuppliersDrawer({ level }: { level: InventoryLevelDetail }) {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const navigate = useNavigate()
	const prompt = usePrompt()
	const variantUuid = level.variant.uuid
	const currencyCode = level.margin?.currencyCode ?? 'ISK'
	const currencyDecimals = getCurrencyDecimals(currencyCode)

	const variantSuppliers = useListVariantSuppliers({ variantUuid, limit: 100, offset: 0 })
	const allSuppliers = useListSuppliers({ limit: 100, offset: 0 })

	const setPreferred = useSetPreferredVariantSupplier(true)
	const upsertSupplier = useUpsertVariantSupplier(true)
	const deleteSupplier = useDeleteVariantSupplier(true)

	const [newSupplierUuid, setNewSupplierUuid] = useState('')
	const [newPurchasePrice, setNewPurchasePrice] = useState('')
	const [newSupplierProductCode, setNewSupplierProductCode] = useState('')
	const [editState, setEditState] = useState<Record<string, { purchasePrice: string; supplierProductCode: string }>>(
		{}
	)

	const linkedSupplierUuids = new Set(variantSuppliers.data?.variantSuppliers.map(vs => vs.supplier.uuid))
	const availableSuppliers = allSuppliers.data?.suppliers.filter(s => !linkedSupplierUuids.has(s.uuid)) ?? []
	const hasNoSuppliers = allSuppliers.isSuccess && (allSuppliers.data?.suppliers.length ?? 0) === 0
	const hasLinkedSuppliers = (variantSuppliers.data?.variantSuppliers.length ?? 0) > 0

	const handleSetPreferred = async (row: VariantSupplier) => {
		if (row.isPreferred) return
		await setPreferred.mutateAsync({ path: { variantSupplierUuid: row.uuid } })
		toast.success(L.Inventory.SupplierDialog.Preferred())
	}

	const handleDelete = async (row: VariantSupplier) => {
		const confirmed = await prompt({
			title: L.General.AreYouSure(),
			description: L.General.Delete()
		})
		if (!confirmed) return
		await deleteSupplier.mutateAsync({ path: { variantSupplierUuid: row.uuid } })
		toast.success(L.General.Delete())
	}

	const startEditing = (row: VariantSupplier) =>
		setEditState(prev => ({
			...prev,
			[row.uuid]: {
				purchasePrice: String(fromPrecision(row.purchasePrice)),
				supplierProductCode: row.supplierProductCode ?? ''
			}
		}))

	const cancelEditing = (uuid: string) =>
		setEditState(prev => {
			const next = { ...prev }
			delete next[uuid]
			return next
		})

	const handleUpdateSupplier = async (row: VariantSupplier) => {
		const next = editState[row.uuid]
		if (!next || next.purchasePrice === '') return
		await upsertSupplier.mutateAsync({
			body: {
				variantUuid,
				supplierUuid: row.supplier.uuid,
				purchasePrice: toPrecision(Number(next.purchasePrice), currencyDecimals),
				currencyCode,
				supplierProductCode: next.supplierProductCode || undefined,
				isPreferred: row.isPreferred
			}
		})
		cancelEditing(row.uuid)
		toast.success(L.Inventory.SupplierDialog.UpdateSuccess())
	}

	const handleAddSupplier = async () => {
		if (!newSupplierUuid || !newPurchasePrice) return
		await upsertSupplier.mutateAsync({
			body: {
				variantUuid,
				supplierUuid: newSupplierUuid,
				purchasePrice: toPrecision(Number(newPurchasePrice), currencyDecimals),
				currencyCode,
				supplierProductCode: newSupplierProductCode || undefined,
				isPreferred: (variantSuppliers.data?.variantSuppliers.length ?? 0) === 0
			}
		})
		setNewSupplierUuid('')
		setNewPurchasePrice('')
		setNewSupplierProductCode('')
		toast.success(L.Inventory.SupplierDialog.AddSupplier())
	}

	const busy = setPreferred.isPending || upsertSupplier.isPending || deleteSupplier.isPending

	return (
		<>
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Inventory.SupplierDialog.Title()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<div className='flex flex-col gap-4 px-1'>
					<div className='flex items-center justify-between gap-2'>
						<Text>{level.variant.productTitle}</Text>
						<Button
							size='small'
							variant='transparent'
							className='shrink-0 gap-x-1'
							onClick={() =>
								void navigate({
									to: '/products/$productUuid/prices',
									params: { productUuid: level.variant.productUuid }
								})
							}
						>
							<PenPlus />
							{L.Inventory.SupplierDialog.EditPrices()}
						</Button>
					</div>
					{level.margin && (
						<div className='rounded-md border border-ui-border-base bg-ui-bg-subtle px-3 py-2 text-sm'>
							<div>
								{L.Inventory.Column.RetailPrice()}:{' '}
								{formatAmount(
									level.margin.retailPrice,
									level.margin.retailCurrencyCode ?? level.margin.currencyCode
								)}
							</div>
							<div className='flex items-center gap-1'>
								<span className='inline-flex items-center gap-1'>
									{L.Inventory.Column.Margin()}:
									<IconButton
										type='button'
										size='2xsmall'
										variant='transparent'
										className='text-ui-fg-muted'
										tooltipText={L.Inventory.SupplierDialog.MarginTaxNote()}
										aria-label={L.Inventory.SupplierDialog.MarginTaxNote()}
									>
										<InformationCircle />
									</IconButton>
								</span>{' '}
								{level.margin.currencyMismatch ? (
									<span className='inline-flex items-center gap-1 text-ui-fg-subtle'>
										{L.Inventory.SupplierDialog.MarginUnavailable()}
										<IconButton
											type='button'
											size='2xsmall'
											variant='transparent'
											className='text-ui-fg-muted'
											tooltipText={L.Inventory.SupplierDialog.MarginCurrencyMismatch({
												retail: level.margin.retailCurrencyCode ?? '?',
												purchase: level.margin.currencyCode ?? '?'
											})}
											aria-label={L.Inventory.SupplierDialog.MarginCurrencyMismatch({
												retail: level.margin.retailCurrencyCode ?? '?',
												purchase: level.margin.currencyCode ?? '?'
											})}
										>
											<ExclamationCircle />
										</IconButton>
									</span>
								) : (
									formatMarginPercent(level.margin.marginPercent)
								)}
							</div>
						</div>
					)}
					{hasNoSuppliers ? (
						<EmptyState
							title={L.Inventory.SupplierDialog.NoSuppliersYet()}
							description={L.Inventory.SupplierDialog.NoSuppliersDescription()}
							action={{
								label: L.Inventory.SupplierDialog.CreateSupplierAction(),
								onClick: () => void navigate({ to: '/inventory/suppliers/create' })
							}}
						/>
					) : (
						<>
							<div className='flex flex-col gap-2'>
								{(variantSuppliers.data?.variantSuppliers ?? []).map(row => {
									const editing = editState[row.uuid]
									return (
										<div
											key={row.uuid}
											className='flex flex-col gap-2 rounded-md border border-ui-border-base px-3 py-2'
										>
											<div className='flex items-center justify-between gap-3'>
												<div className='min-w-0 flex-1'>
													<div className='font-medium'>{row.supplier.name}</div>
													{!editing && (
														<div className='text-sm text-ui-fg-subtle'>
															{formatAmount(row.purchasePrice, row.currencyCode)}
															{row.supplierProductCode
																? ` · ${row.supplierProductCode}`
																: ''}
														</div>
													)}
												</div>
												<div className='flex shrink-0 items-center gap-2'>
													{row.isPreferred ? (
														<Text className='text-sm text-ui-fg-subtle'>
															{L.Inventory.SupplierDialog.Preferred()}
														</Text>
													) : (
														<Button
															size='small'
															variant='secondary'
															disabled={busy}
															onClick={() => handleSetPreferred(row)}
														>
															{L.Inventory.SupplierDialog.SetPreferred()}
														</Button>
													)}
													{!editing && (
														<Button
															size='small'
															variant='secondary'
															disabled={busy}
															onClick={() => startEditing(row)}
														>
															{L.General.Edit()}
														</Button>
													)}
													<Button
														size='small'
														variant='transparent'
														disabled={busy}
														onClick={() => handleDelete(row)}
													>
														{L.General.Delete()}
													</Button>
												</div>
											</div>
											{editing && (
												<div className='flex flex-col gap-2 border-t border-ui-border-base pt-2'>
													<div className='flex flex-col gap-1'>
														<Label>{L.Inventory.Column.PurchasePrice()}</Label>
														<CurrencyInput
															code={currencyCode}
															symbol={getSymbolFromCurrency(currencyCode) ?? currencyCode}
															value={editing.purchasePrice}
															onValueChange={value =>
																setEditState(prev => ({
																	...prev,
																	[row.uuid]: {
																		...prev[row.uuid],
																		purchasePrice: value ?? ''
																	}
																}))
															}
														/>
														<p className='text-xs text-ui-fg-subtle'>
															{L.Inventory.SupplierDialog.PurchasePriceTaxNote()}
														</p>
													</div>
													<div className='flex flex-col gap-1'>
														<Label>
															{L.Inventory.SupplierDialog.SupplierProductCode()}
														</Label>
														<Input
															value={editing.supplierProductCode}
															onChange={e =>
																setEditState(prev => ({
																	...prev,
																	[row.uuid]: {
																		...prev[row.uuid],
																		supplierProductCode: e.target.value
																	}
																}))
															}
														/>
													</div>
													<div className='flex items-center justify-end gap-2'>
														<Button
															size='small'
															variant='secondary'
															disabled={busy}
															onClick={() => cancelEditing(row.uuid)}
														>
															{L.General.Cancel()}
														</Button>
														<Button
															size='small'
															disabled={busy || editing.purchasePrice === ''}
															onClick={() => handleUpdateSupplier(row)}
														>
															{L.General.Save()}
														</Button>
													</div>
												</div>
											)}
										</div>
									)
								})}
								{(variantSuppliers.data?.variantSuppliers.length ?? 0) === 0 && (
									<Text className='text-sm text-ui-fg-subtle'>
										{L.Inventory.SupplierDialog.Empty()}
									</Text>
								)}
							</div>
							{availableSuppliers.length > 0 ? (
								<div className='flex flex-col gap-2 border-t border-ui-border-base pt-4'>
									<Label>{L.Inventory.SupplierDialog.AddSupplier()}</Label>
									<Select
										value={newSupplierUuid || ALL_VALUE}
										onValueChange={v => setNewSupplierUuid(v === ALL_VALUE ? '' : v)}
									>
										<Select.Trigger>
											<Select.Value placeholder={L.Inventory.SupplierDialog.SelectSupplier()} />
										</Select.Trigger>
										<Select.Content>
											<Select.Item value={ALL_VALUE}>—</Select.Item>
											{availableSuppliers.map(supplier => (
												<Select.Item key={supplier.uuid} value={supplier.uuid}>
													{supplier.name}
												</Select.Item>
											))}
										</Select.Content>
									</Select>
									<div className='flex flex-col gap-1'>
										<Label>{L.Inventory.Column.PurchasePrice()}</Label>
										<CurrencyInput
											code={currencyCode}
											symbol={getSymbolFromCurrency(currencyCode) ?? currencyCode}
											value={newPurchasePrice}
											onValueChange={value => setNewPurchasePrice(value ?? '')}
										/>
										<p className='text-xs text-ui-fg-subtle'>
											{L.Inventory.SupplierDialog.PurchasePriceTaxNote()}
										</p>
									</div>
									<div className='flex flex-col gap-1'>
										<Label>{L.Inventory.SupplierDialog.SupplierProductCode()}</Label>
										<Input
											value={newSupplierProductCode}
											onChange={e => setNewSupplierProductCode(e.target.value)}
										/>
									</div>
								</div>
							) : hasLinkedSuppliers ? (
								<div className='flex flex-col gap-2 border-t border-ui-border-base pt-4'>
									<Text className='text-sm text-ui-fg-subtle'>
										{L.Inventory.SupplierDialog.AllSuppliersLinked()}
									</Text>
									<Button
										size='small'
										variant='secondary'
										className='self-start'
										onClick={() => void navigate({ to: '/inventory/suppliers/create' })}
									>
										{L.Inventory.SupplierDialog.CreateSupplierAction()}
									</Button>
								</div>
							) : null}
						</>
					)}
				</div>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small' onClick={() => handleSuccess()}>
							{L.General.Close()}
						</Button>
					</Drawer.Close>
					{availableSuppliers.length > 0 && (
						<Button
							size='small'
							disabled={!newSupplierUuid || !newPurchasePrice || busy}
							onClick={handleAddSupplier}
						>
							{L.Inventory.SupplierDialog.AddSupplier()}
						</Button>
					)}
				</div>
			</Drawer.Footer>
		</>
	)
}
